// One call — /voice-tickets/:callId. Who called, when, on which line (header
// card, previous / next call of the same number, client record), what the
// agent did (tickets opened, SMS sent, hand-overs, what it looked up), the
// conversation with the agent's tool calls inline (arguments behind
// « Détails », never raw JSON in front), then this call's facts, the
// caller's memory profile and the other calls of the same number.
// API: GET /api/ai-voice/conversations/{callId}, GET /api/ai-voice/memory
// ?phone_number (404 = no profile), GET /api/ai-voice/conversations
// ?phone_number&limit=200 (same caller: prev / next), GET /api/voice-agents
// (line name when the call comes back without assistant_name).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bot, ChevronLeft, ChevronRight, CircleAlert, CircleCheck, Contact, History, ListChecks, MessagesSquare, Phone,
  PhoneForwarded, Search, UserRound,
} from 'lucide-react';
import {
  Badge, Card, DescriptionList, EmptyState, IconChip,
} from '../design/ui';
import { formatShortDate, parseUtc } from '../utils/dates';
import { callerInitials, formatCallDay, formatCallFull, formatCallTime } from '../utils/voiceFormat';
import { formatPhone } from './voiceSetup/phoneFormat';
import { formatDuration, listAgents } from './voiceSetup/api';
import {
  agentIndex, callKind, callOutcomes, lineAgentName, toolMeta, toolOutcomeLabel,
} from './voiceSetup/voiceModel';
import { TranscriptTurn, pluralT } from './voiceSetup/VoiceKit';
import './voiceSetup/VoiceSetup.css';
import './VoiceTickets.css';
import './VoiceTicketDetail.css';

const KIND_ICON = { action: CircleCheck, escalation: PhoneForwarded, lookup: Search };
const KIND_TONE = { action: 'success', escalation: 'warning', lookup: 'neutral' };

// Argument names the platform voice tools use, in the operator's words.
const ARG_LABELS = {
  tracking_number: ['voiceUi.args.tracking', 'Numéro de suivi'],
  carrier: ['voiceUi.args.carrier', 'Transporteur'],
  subject: ['voiceUi.args.subject', 'Objet'],
  customer_email: ['voiceUi.args.email', 'E-mail'],
  customer_name: ['voiceUi.args.name', 'Nom'],
  description: ['voiceUi.args.description', 'Description'],
  message: ['voiceUi.args.message', 'Message'],
  language: ['voiceUi.args.language', 'Langue'],
  date: ['voiceUi.args.date', 'Date'],
  time: ['voiceUi.args.time', 'Heure'],
  people: ['voiceUi.args.people', 'Personnes'],
};
const SUMMARY_KEYS = ['subject', 'tracking_number', 'carrier', 'customer_email'];

const prettify = (name) => String(name || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const argLabel = (key, t) => (ARG_LABELS[key] ? t(ARG_LABELS[key][0], ARG_LABELS[key][1]) : prettify(key));
const shortDay = (value) => formatShortDate(value, undefined, { empty: '' });
const durationText = (seconds) => (seconds === null || seconds === undefined ? '—' : formatDuration(seconds));

// A tool call's arguments as one short line: known keys first, then up to
// two leftover primitives — the full arguments stay behind « Détails ».
const summarizeArgs = (args, t) => {
  if (!args || typeof args !== 'object') return '';
  const parts = [];
  const date = shortDay(args.date);
  if (date) parts.push(date);
  if (args.time) parts.push(String(args.time));
  if (args.people != null) parts.push(t('voiceTickets.detail.peopleShort', '{{n}} pers.', { n: args.people }));
  SUMMARY_KEYS.forEach((key) => {
    if (typeof args[key] === 'string' && args[key].trim()) parts.push(args[key].trim().slice(0, 60));
  });
  const known = new Set(['date', 'time', 'people', 'description', 'language', ...SUMMARY_KEYS]);
  Object.entries(args)
    .filter(([key, value]) => !known.has(key) && (typeof value === 'string' || typeof value === 'number'))
    .slice(0, 2)
    .forEach(([key, value]) => parts.push(`${argLabel(key, t)} : ${String(value).slice(0, 40)}`));
  return parts.join(' · ');
};

const argItems = (args, t) => Object.entries(args || {})
  .filter(([, value]) => value !== null && value !== undefined && value !== '')
  .map(([key, value]) => ({
    label: argLabel(key, t),
    value: typeof value === 'object' ? JSON.stringify(value) : String(value),
  }));

const ToolEvent = ({ call, t }) => {
  const [open, setOpen] = useState(false);
  const { kind } = toolMeta(call.name);
  if (kind === 'ignore') return null;
  const label = toolOutcomeLabel(call.name, t);
  const items = argItems(call.arguments, t);
  const hasDetails = items.length > 0 || Boolean(call.result);
  return (
    <li className="voice-call__event">
      <div className="voice-call__event-line">
        <IconChip icon={KIND_ICON[kind] || Search} tone={KIND_TONE[kind]} size="sm" />
        <span className="voice-call__event-label">{label}</span>
        {call.ts && <time className="voice-call__event-time">{formatCallTime(call.ts)}</time>}
        {hasDetails && (
          <button
            type="button"
            className="u-btn u-btn--ghost u-btn--sm voice-v2__disclosure"
            aria-expanded={open}
            aria-label={t('voiceUi.call.detailsOf', 'Détails : {{label}}', { label })}
            onClick={() => setOpen(!open)}
          >
            <ChevronRight aria-hidden="true" />
            {t('voiceUi.call.details', 'Détails')}
          </button>
        )}
      </div>
      {open && (
        <div className="voice-call__event-details">
          {items.length > 0 && <DescriptionList items={items} />}
          {call.result && (
            <div className="voice-call__event-result">
              <span className="u-overline">{t('voiceUi.call.toolAnswer', 'Réponse de l’outil')}</span>
              <p>{String(call.result)}</p>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

const VoiceTicketDetail = () => {
  const { t } = useTranslation();
  const tp = pluralT(t);
  const navigate = useNavigate();
  const { callId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [memory, setMemory] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchConversation = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/ai-voice/conversations/${encodeURIComponent(callId)}`, { headers });
      const data = response.data || null;
      setConversation(data);

      if (data?.phone_number) {
        // Profile + the caller's other calls (prev/next navigation), best effort.
        const [memoryResult, siblingsResult] = await Promise.allSettled([
          axios.get(`/api/ai-voice/memory?phone_number=${encodeURIComponent(data.phone_number)}`, { headers }),
          axios.get(`/api/ai-voice/conversations?phone_number=${encodeURIComponent(data.phone_number)}&limit=200`, { headers }),
        ]);
        setMemory(memoryResult.status === 'fulfilled' ? memoryResult.value.data || null : null);
        setSiblings(siblingsResult.status === 'fulfilled' ? siblingsResult.value.data?.conversations || [] : []);
      }
    } catch (err) {
      console.error('Failed to fetch voice conversation:', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('voiceTickets.detail.fetchError', 'Échec du chargement de la conversation.'));
    } finally {
      setLoading(false);
    }
  }, [callId, headers, t]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  useEffect(() => {
    let alive = true;
    listAgents()
      .then((res) => alive && setAgents(res.data?.agents || []))
      .catch(() => alive && setAgents([]));
    return () => { alive = false; };
  }, []);

  // Transcript turns and tool events merged into one chronological timeline.
  const timeline = useMemo(() => {
    if (!conversation) return [];
    const items = [];
    (conversation.transcript || []).forEach((turn, index) => {
      items.push({ type: 'turn', ts: parseUtc(turn.ts)?.getTime() ?? index, turn, key: `turn-${index}` });
    });
    (conversation.tool_calls || []).forEach((call, index) => {
      const ts = parseUtc(call.ts)?.getTime();
      items.push({ type: 'event', ts: ts ?? Number.MAX_SAFE_INTEGER - index, call, key: `event-${index}` });
    });
    return items.sort((a, b) => a.ts - b.ts);
  }, [conversation]);

  const { prevCall, nextCall } = useMemo(() => {
    const index = siblings.findIndex((conv) => conv.call_id === callId);
    if (index === -1) return { prevCall: null, nextCall: null };
    // siblings are sorted newest first: "previous call" = the older one.
    return { prevCall: siblings[index + 1] || null, nextCall: siblings[index - 1] || null };
  }, [siblings, callId]);

  if (loading) {
    return (
      <div className="u-page voice-v2 voice-call" aria-busy="true">
        <section className="u-card voice-call__hero">
          <div className="voice-call__identity">
            <span className="u-skeleton voice-call__avatar-skeleton" />
            <div className="voice-call__identity-text">
              <span className="u-skeleton voice-v2__line-skeleton" />
              <span className="u-skeleton voice-v2__title-skeleton" />
            </div>
          </div>
        </section>
        <div className="u-well">
          <section className="u-card voice-v2__list-skeleton" aria-label={t('voiceTickets.detail.loading', 'Chargement de la conversation…')}>
            {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton" />)}
          </section>
        </div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="u-page voice-v2 voice-call">
        <section className="u-card">
          <EmptyState
            icon={CircleAlert}
            title={error || t('voiceTickets.detail.notFound', 'Conversation introuvable.')}
            action={<Link className="u-btn u-btn--secondary u-btn--sm" to="/voice-tickets">{t('voiceUi.call.back', 'Retour aux appels')}</Link>}
          />
        </section>
      </div>
    );
  }

  const index = agentIndex(agents);
  const callerName = conversation.caller_name || memory?.caller_name || null;
  const toolNames = conversation.tool_names || (conversation.tool_calls || []).map((c) => c.name);
  const kind = callKind(toolNames);
  const lineName = lineAgentName(conversation, index);
  const lineAgent = index.byId.get(conversation.assistant_id) || index.byNumber.get(conversation.dialed_number) || null;
  const turns = (conversation.transcript || []).length;
  const lineLabel = lineName
    ? t('voiceUi.call.lineNamed', 'Ligne {{name}} · {{number}}', { name: lineName, number: formatPhone(conversation.dialed_number) || '—' })
    : t('voiceUi.call.lineNumber', 'Ligne {{number}}', { number: formatPhone(conversation.dialed_number) || '—' });

  const toolCalls = (conversation.tool_calls || []).filter((call) => toolMeta(call.name).kind !== 'ignore');
  const acted = toolCalls.filter((call) => ['action', 'escalation'].includes(toolMeta(call.name).kind));
  const lookups = callOutcomes(toolNames, t, { includeLookups: true }).filter((outcome) => outcome.kind === 'lookup');

  const kindBadge = {
    action: <Badge tone="success" dot>{t('voiceUi.call.kindAction', 'Avec action')}</Badge>,
    escalated: <Badge tone="warning" dot>{t('voiceUi.call.kindEscalated', 'Escaladé')}</Badge>,
    info: <Badge dot>{t('voiceTickets.facetInfo', 'Info seule')}</Badge>,
  }[kind];

  const renderHero = () => (
    <section className="u-card voice-call__hero">
      <div className="voice-call__identity">
        <span className={`u-avatar u-avatar--lg${callerName ? '' : ' voice-call__avatar--unknown'}`} aria-hidden="true">
          {callerInitials(callerName, conversation.phone_number)}
        </span>
        <div className="voice-call__identity-text">
          <nav className="u-breadcrumb" aria-label={t('voiceUi.breadcrumb', 'Fil d’Ariane')}>
            <Link to="/voice-tickets">{t('voiceUi.calls.title', 'Appels')}</Link>
            <ChevronRight className="voice-v2__crumb-sep" aria-hidden="true" />
          </nav>
          <h1 className="u-page-title">{callerName || formatPhone(conversation.phone_number) || t('voiceTickets.detail.unknownCaller', 'Appelant inconnu')}</h1>
          <div className="voice-call__meta">
            {kindBadge}
            {callerName && conversation.phone_number && (
              <span className="voice-call__meta-item u-num"><Phone aria-hidden="true" />{formatPhone(conversation.phone_number)}</span>
            )}
            <span className="voice-call__meta-item u-num">
              {formatCallFull(conversation.started_at)} · {durationText(conversation.duration_seconds)}
            </span>
            <span className="voice-call__meta-item"><Bot aria-hidden="true" />{lineLabel}</span>
          </div>
        </div>
        <div className="voice-call__actions">
          <div className="voice-call__nav">
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--sm"
              disabled={!prevCall}
              onClick={() => prevCall && navigate(`/voice-tickets/${encodeURIComponent(prevCall.call_id)}`)}
              title={t('voiceUi.call.prevTitle', 'Appel précédent de ce numéro')}
            >
              <ChevronLeft aria-hidden="true" />
              {t('voiceTickets.detail.prevCall', 'Appel préc.')}
            </button>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--sm"
              disabled={!nextCall}
              onClick={() => nextCall && navigate(`/voice-tickets/${encodeURIComponent(nextCall.call_id)}`)}
              title={t('voiceUi.call.nextTitle', 'Appel suivant de ce numéro')}
            >
              {t('voiceTickets.detail.nextCall', 'Appel suiv.')}
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
          {conversation.client_id && (
            <button type="button" className="u-btn u-btn--secondary" onClick={() => navigate(`/clients/${conversation.client_id}`)}>
              <Contact aria-hidden="true" />
              {t('voiceTickets.detail.clientLink', 'Fiche client')}
            </button>
          )}
        </div>
      </div>
    </section>
  );

  const renderOutcome = () => (
    <Card
      icon={ListChecks}
      title={t('voiceUi.call.outcomeTitle', 'Ce que l’agent a fait')}
      description={acted.length
        ? tp('voiceUi.call.outcomeCount', '{{count}} action pendant l’appel', '{{count}} actions pendant l’appel', { count: acted.length })
        : t('voiceUi.call.outcomeNone', 'L’agent a renseigné l’appelant sans rien créer ni transférer.')}
      flush={acted.length > 0 || lookups.length > 0}
    >
      {acted.length > 0 && (
        <ul className="u-list">
          {acted.map((call, position) => {
            const meta = toolMeta(call.name);
            const summary = summarizeArgs(call.arguments, t);
            return (
              <li key={`${call.name}-${position}`} className="u-row">
                <IconChip icon={KIND_ICON[meta.kind]} tone={KIND_TONE[meta.kind]} size="sm" />
                <div className="u-row__main">
                  <div className="u-row__title">{toolOutcomeLabel(call.name, t)}</div>
                  {summary && <div className="u-row__meta u-row__meta--wrap">{summary}</div>}
                </div>
                {call.ts && <span className="u-row__side voice-call__event-time">{formatCallTime(call.ts)}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {lookups.length > 0 && (
        <div className="voice-call__lookups">
          <span className="u-overline">{t('voiceUi.call.lookedUp', 'Consulté')}</span>
          <span className="voice-call__badges">
            {lookups.map((outcome) => <Badge key={outcome.name}>{outcome.label}</Badge>)}
          </span>
        </div>
      )}
    </Card>
  );

  const renderConversation = () => (
    <Card
      icon={MessagesSquare}
      title={t('voiceTickets.detail.conversation', 'Conversation')}
      description={tp('voiceUi.call.conversationDesc', '{{count}} échange · les outils utilisés par l’agent apparaissent à leur place', '{{count}} échanges · les outils utilisés par l’agent apparaissent à leur place', { count: turns })}
    >
      {timeline.length === 0 ? (
        <EmptyState icon={MessagesSquare} title={t('voiceTickets.detail.noTranscript', 'Aucune transcription disponible pour cet appel.')} />
      ) : (
        <ol className="voice-v2__transcript">
          {timeline.map((item) => (item.type === 'event' ? (
            <ToolEvent key={item.key} call={item.call} t={t} />
          ) : (
            <TranscriptTurn
              key={item.key}
              role={item.turn.role === 'caller' ? 'caller' : 'agent'}
              who={item.turn.role === 'caller'
                ? (callerName || t('voiceUi.call.caller', 'Appelant'))
                : (lineName || t('voiceUi.call.agent', 'Agent vocal'))}
              icon={item.turn.role === 'caller' ? UserRound : Bot}
              text={item.turn.text}
              time={item.turn.ts ? formatCallTime(item.turn.ts) : null}
            />
          )))}
        </ol>
      )}
    </Card>
  );

  const renderFacts = () => (
    <Card icon={Phone} title={t('voiceTickets.detail.thisCall', 'Cet appel')}>
      <DescriptionList items={[
        {
          label: t('voiceTickets.detail.voiceAgent', 'Agent vocal'),
          value: lineName && lineAgent
            ? <Link className="u-link" to={`/voice-agents/${lineAgent.id}`}>{lineName}</Link>
            : lineName,
        },
        { label: t('voiceTickets.detail.dialedLine', 'Ligne appelée'), value: conversation.dialed_number ? <span className="u-num">{formatPhone(conversation.dialed_number)}</span> : null },
        { label: t('voiceUi.call.started', 'Début'), value: <span className="u-num">{formatCallFull(conversation.started_at)}</span> },
        { label: t('voiceUi.call.ended', 'Fin'), value: conversation.ended_at ? <span className="u-num">{formatCallTime(conversation.ended_at)}</span> : null },
        { label: t('voiceTickets.duration', 'Durée'), value: <span className="u-num">{durationText(conversation.duration_seconds)}</span> },
        { label: t('voiceTickets.turns', 'Échanges'), value: <span className="u-num">{turns}</span> },
      ]}
      />
    </Card>
  );

  const renderProfile = () => {
    const pastRequests = Array.isArray(memory?.past_requests) ? memory.past_requests.slice(-6).reverse() : [];
    return (
      <Card icon={UserRound} title={t('voiceTickets.detail.callerProfile', 'Profil de l’appelant')}>
        {!memory ? (
          <p className="voice-call__muted">
            {t('voiceTickets.detail.noMemory', 'Aucun profil mémoire pour cet appelant pour le moment.')}
          </p>
        ) : (
          <div className="voice-call__profile">
            <DescriptionList items={[
              { label: t('voiceTickets.detail.name', 'Nom'), value: memory.caller_name },
              { label: t('voiceTickets.detail.email', 'E-mail'), value: memory.email },
              { label: t('voiceTickets.detail.address', 'Adresse'), value: memory.postal_address },
              { label: t('voiceTickets.detail.callCount', 'Appels'), value: t('voiceTickets.detail.callsTotal', '{{n}} au total', { n: memory.call_count ?? 0 }) },
            ]}
            />
            {memory.summary && <p className="voice-call__summary">{memory.summary}</p>}
            {Array.isArray(memory.preferences) && memory.preferences.length > 0 && (
              <div className="voice-call__group">
                <span className="u-overline">{t('voiceTickets.detail.preferences', 'Préférences')}</span>
                <span className="voice-call__badges">
                  {memory.preferences.map((pref, position) => <span key={position} className="u-tag">{pref}</span>)}
                </span>
              </div>
            )}
            {pastRequests.length > 0 && (
              <div className="voice-call__group">
                <span className="u-overline">{t('voiceTickets.detail.pastRequests', 'Demandes précédentes')}</span>
                <ul className="voice-call__past">
                  {pastRequests.map((req, position) => {
                    const isObject = typeof req === 'object' && req !== null;
                    const date = isObject ? parseUtc(req.date) : null;
                    return (
                              <li key={position}>
                        {date && <time className="u-num">{formatShortDate(date)}</time>}
                        <span>{isObject ? `${req.request || ''}${req.outcome ? ` (${req.outcome})` : ''}`.trim() : String(req)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  const renderSiblings = () => {
    if (siblings.length < 2) return null;
    const shown = siblings.slice(0, 6);
    return (
      <Card
        icon={History}
        title={t('voiceUi.call.siblingsTitle', 'Appels de ce numéro')}
        description={siblings.length >= 200
          ? t('voiceUi.call.siblingsMany', '200 derniers appels de ce numéro')
          : tp('voiceUi.call.siblingsCount', '{{count}} appel de ce numéro', '{{count}} appels de ce numéro', { count: siblings.length })}
        flush
      >
        <ul className="u-list">
          {shown.map((conv) => {
            const outcomes = callOutcomes(conv.tool_names, t);
            const current = conv.call_id === callId;
            const content = (
              <>
                <div className="u-row__main">
                  <div className="u-row__title">
                    <span className="u-num">{formatCallDay(conv.started_at)}</span>
                    {current && <Badge tone="accent">{t('voiceUi.call.current', 'Affiché')}</Badge>}
                  </div>
                  <div className="u-row__meta">
                    {outcomes.length ? outcomes.map((outcome) => outcome.label).join(' · ') : t('voiceTickets.facetInfo', 'Info seule')}
                  </div>
                </div>
                <div className="u-row__side">
                  <span className="u-num voice-v2__duration">{durationText(conv.duration_seconds)}</span>
                  {!current && <ChevronRight className="voice-v2__chevron" aria-hidden="true" />}
                </div>
              </>
            );
            return (
              <li key={conv.call_id}>
                {current
                  ? <div className="u-row voice-call__sibling--current">{content}</div>
                  : <Link className="u-row voice-v2__link-row" to={`/voice-tickets/${encodeURIComponent(conv.call_id)}`}>{content}</Link>}
              </li>
            );
          })}
        </ul>
      </Card>
    );
  };

  return (
    <div className="u-page voice-v2 voice-call">
      {renderHero()}
      <div className="u-well">
        <div className="voice-v2__layout voice-call__layout">
          <div className="voice-v2__main">
            {renderOutcome()}
            {renderConversation()}
          </div>
          <aside className="voice-v2__aside">
            {renderFacts()}
            {renderProfile()}
            {renderSiblings()}
          </aside>
        </div>
      </div>
    </div>
  );
};

// Exported for the tests: the « Détails » summary must stay human.
export { summarizeArgs };

export default VoiceTicketDetail;
